/**
 * A fanned hand of selectable cards, scrollable horizontally on every
 * device.
 *
 * Layout:
 *   .my-hand        – the scroll viewport (full-width, overflow-x: auto)
 *   .my-hand-inner  – the fan itself; `margin: auto` centres it inside the
 *                     viewport when the cards fit, and lets the overflow
 *                     scroll naturally when they don't.
 *
 * Each card wrapper has an explicit z-index based on its position so the
 * rightmost overlap edge is always painted on top — including over a
 * selected card that's been transformed upward.
 */

import { useEffect, useMemo, useRef } from "react";
import type { CardId, SuitId } from "../../../shared/games/shengji.js";
import { PlayingCard, type CardDecorator } from "./PlayingCard.js";

/**
 * Per-game hand-sort variants.
 *
 *   "default" — Sheng Ji style: trump first, then suit groups; ace high.
 *               Honors `trumpRank` + `trumpSuit`.
 *   "aces-low" — Suit groups in S/H/C/D order with A as the LOWEST rank
 *               (sorted before 2). Jokers (if any) at the far right.
 *   "swoop"    — Suit groups in S/H/C/D order, A low. 10s and Jokers
 *               (wild swoop cards) are pushed to the far right of the
 *               whole hand regardless of suit.
 *   Custom function — full control; return a comparable key per card.
 */
export type HandSortMode = "default" | "aces-low" | "swoop";
export type HandSortKey = (id: CardId) => readonly number[];

interface HandProps {
  cards: CardId[];
  legal?: Set<CardId>;
  selected: Set<number>;
  onToggle: (index: number) => void;
  cardWidth?: number;
  /** Sheng Ji-only: trump rank/suit cluster cards to the right. */
  trumpRank?: string;
  trumpSuit?: SuitId | null;
  /** Per-card SVG overlay (e.g. Sheng Ji point stripe, Hearts marker). */
  decoratorOf?: (id: CardId) => CardDecorator;
  /**
   * Choose a preset sort mode, or pass a sortKey for full control.
   * Defaults to "default" (Sheng Ji rules) when omitted.
   */
  sort?: HandSortMode | HandSortKey;
  /**
   * UA-detected mobile device. Toggles smaller cards and a darker backdrop.
   * The layout itself (scroll + centre via margin: auto) is unchanged.
   */
  mobile?: boolean;
}

const SUIT_NON_TRUMP_ORDER: Record<string, number> = { S: 0, H: 1, C: 2, D: 3 };
const RANK_VAL_DEFAULT: Record<string, number> = {
  "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
  "T": 10, "J": 11, "Q": 12, "K": 13, "A": 14,
};
const RANK_VAL_ACES_LOW: Record<string, number> = {
  "A": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
  "T": 10, "J": 11, "Q": 12, "K": 13,
};

function defaultSortKey(
  id: CardId,
  trumpRank?: string,
  trumpSuit?: SuitId | null,
): readonly number[] {
  if (id === "SJ") return [13, 0];
  if (id === "BJ") return [14, 0];
  const r = id[0]!;
  const s = id[1]!;
  if (trumpRank !== undefined) {
    if (r === trumpRank) {
      if (trumpSuit && s === trumpSuit) return [12, 0];
      return [11, SUIT_NON_TRUMP_ORDER[s] ?? 0];
    }
    if (trumpSuit && s === trumpSuit) {
      return [10, RANK_VAL_DEFAULT[r] ?? 0];
    }
  }
  return [SUIT_NON_TRUMP_ORDER[s] ?? 0, RANK_VAL_DEFAULT[r] ?? 0];
}

function acesLowSortKey(id: CardId): readonly number[] {
  // Jokers (if any) at the far right.
  if (id === "SJ") return [9, 0];
  if (id === "BJ") return [9, 1];
  const r = id[0]!;
  const s = id[1]!;
  return [SUIT_NON_TRUMP_ORDER[s] ?? 0, RANK_VAL_ACES_LOW[r] ?? 0];
}

function swoopSortKey(id: CardId): readonly number[] {
  // Suit doesn't matter in Swoop — sort by rank only. Wilds (10s and
  // jokers) cluster at the far right.
  if (id === "SJ") return [100, 2];
  if (id === "BJ") return [100, 3];
  const r = id[0]!;
  if (r === "T") return [100, 1];
  return [RANK_VAL_ACES_LOW[r] ?? 0, 0];
}

function resolveSortKey(
  sort: HandSortMode | HandSortKey | undefined,
  trumpRank: string | undefined,
  trumpSuit: SuitId | null | undefined,
): (id: CardId) => readonly number[] {
  if (typeof sort === "function") return sort;
  switch (sort) {
    case "aces-low": return acesLowSortKey;
    case "swoop": return swoopSortKey;
    default: return (id) => defaultSortKey(id, trumpRank, trumpSuit);
  }
}

function sortCards(
  cards: CardId[],
  keyFn: (id: CardId) => readonly number[],
): { id: CardId; originalIndex: number }[] {
  return cards
    .map((id, originalIndex) => ({ id, originalIndex }))
    .sort((a, b) => {
      const ka = keyFn(a.id);
      const kb = keyFn(b.id);
      const n = Math.max(ka.length, kb.length);
      for (let i = 0; i < n; i++) {
        const av = ka[i] ?? 0;
        const bv = kb[i] ?? 0;
        if (av !== bv) return av - bv;
      }
      return 0;
    });
}

/**
 * Dynamic overlap so a 25- or 33-card hand still fits the target on
 * desktop; on narrow screens the inner naturally exceeds the viewport
 * and the scroll container takes over.
 */
const HAND_TARGET_WIDTH = 1140;
const MOST_OVERLAP = -74;
const LEAST_OVERLAP = -10;

function computeOverlap(n: number, cardWidth: number): number {
  if (n <= 1) return 0;
  const desired = (HAND_TARGET_WIDTH - n * cardWidth) / (n - 1);
  return Math.max(MOST_OVERLAP, Math.min(LEAST_OVERLAP, desired));
}

export function Hand({
  cards,
  legal,
  selected,
  onToggle,
  cardWidth,
  trumpRank,
  trumpSuit,
  decoratorOf,
  sort,
  mobile = false,
}: HandProps) {
  // Mobile hand cards: 72 (was 82). The smaller size lets the hand
  // strip be ~20 px shorter, giving the board (table) more vertical
  // room — important for 5-6 player layouts where seats need to
  // spread out around the ellipse without crowding the centre pile.
  // Cards are still legible at 72; the top-half crop on rotated
  // phones leaves rank + suit fully visible.
  const w = cardWidth ?? (mobile ? 72 : 104);
  const sorted = useMemo(
    () => sortCards(cards, resolveSortKey(sort, trumpRank, trumpSuit)),
    [cards, sort, trumpRank, trumpSuit]
  );
  const overlap = computeOverlap(cards.length, w);

  // Phone-landscape touch-scroll bridge. The hand uses `overflow-x: auto`
  // for horizontal scrolling — natural on desktop and on un-rotated
  // mobile. But our phone-rotation CSS rotates the entire `.game-shell`
  // 90°, so what the user perceives as a horizontal swipe is physically
  // a vertical swipe. The browser detects scroll-gesture direction in
  // PHYSICAL coords (transforms don't propagate to gesture handling),
  // so a horizontal-user swipe never reaches the hand's overflow-x.
  //
  // This effect manually wires touch events: on touchmove with vertical
  // delta, it adjusts scrollLeft so the user's natural horizontal-in-view
  // swipe scrolls the hand. Only active when the rotation media query
  // would be matching (mobile-device + portrait orientation).
  const handRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!mobile) return undefined;
    const el = handRef.current;
    if (!el) return undefined;
    let active = false;
    let startY = 0;
    let startScroll = 0;
    let dragged = false;
    const DRAG_THRESHOLD = 5;
    function isRotated(): boolean {
      return (
        document.body.classList.contains("mobile-device") &&
        document.body.classList.contains("in-game-view") &&
        window.matchMedia("(orientation: portrait)").matches
      );
    }
    function onTouchStart(e: TouchEvent) {
      if (!isRotated() || !e.touches[0]) return;
      active = true;
      dragged = false;
      startY = e.touches[0].clientY;
      startScroll = el!.scrollLeft;
    }
    function onTouchMove(e: TouchEvent) {
      if (!active || !e.touches[0]) return;
      const dy = e.touches[0].clientY - startY;
      // Don't react to micro-movements — preserves the user's tap
      // intent (card selection) for brief touches.
      if (Math.abs(dy) < DRAG_THRESHOLD && !dragged) return;
      dragged = true;
      // Physical-y INCREASES as the finger moves downward, which the
      // user perceives as a swipe to the RIGHT in their landscape view
      // (because the phone is rotated 90° in CSS). A right-user swipe
      // should scroll the hand to the LEFT in user view — which means
      // scrollLeft DECREASES (revealing content that's currently
      // off-screen to the user's left).
      el!.scrollLeft = startScroll - dy;
      // Prevent the browser's default behavior so iOS Safari doesn't
      // try to bounce the page; we're driving the scroll manually.
      if (e.cancelable) e.preventDefault();
    }
    function onTouchEnd() { active = false; }
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    // touchmove needs to be non-passive so we can preventDefault.
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [mobile]);

  return (
    <div
      ref={handRef}
      className={`my-hand ${mobile ? "compact" : ""}`}
      // Expose the live card width as a CSS variable so the rotation
      // media query in global.css can crop the hand to the top of each
      // card on phones without re-rendering — see the
      // `body.mobile-device.in-game-view .my-hand .playing-card` rule.
      style={{ ["--hand-card-w" as string]: `${w}px` }}
    >
      <div className="my-hand-inner">
        {sorted.map((c, i) => {
          const dimmed = legal !== undefined && !legal.has(c.id);
          const isSelected = selected.has(c.originalIndex);
          const isLast = i === sorted.length - 1;
          return (
            <div
              key={`${c.id}-${c.originalIndex}`}
              className="hand-card-wrapper"
              style={{
                position: "relative",
                zIndex: i + 1,
                marginRight: isLast ? 0 : overlap,
              }}
            >
              <PlayingCard
                card={c.id}
                width={w}
                selected={isSelected}
                dimmed={dimmed}
                selectable={!dimmed}
                onClick={() => onToggle(c.originalIndex)}
                decorator={decoratorOf?.(c.id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
